import { v7 as uuidv7, validate as validateUuid } from 'uuid';

import { deleteCookie, readCookie, writeCookie } from './cookies';

const visitorCookieName = 'mutiny.user.token';
const legacyVisitorQueryParam = '_mv';
const legacyImmortalVisitorCookieName = '_immortal|mutiny.user.token';

function normalizeVisitorIdCandidate(value: string | null): string | null {
  if (value === null) {
    return null;
  }

  const trimmedValue = value.trim();
  if (trimmedValue === '' || trimmedValue.toLowerCase() === 'null') {
    return null;
  }

  return validateUuid(trimmedValue) ? trimmedValue : null;
}

function readQueryParam(name: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return new URL(window.location.href).searchParams.get(name);
  } catch {
    return null;
  }
}

export function getVisitorId(): string {
  // Keep the same browser-token precedence as the legacy Mutiny client; Ruby stores this value as visitors.token.
  const queryVisitorId = normalizeVisitorIdCandidate(readQueryParam(legacyVisitorQueryParam));
  if (queryVisitorId) {
    writeCookie(visitorCookieName, queryVisitorId);
    return queryVisitorId;
  }

  const existingVisitorId = normalizeVisitorIdCandidate(readCookie(visitorCookieName));
  if (existingVisitorId) {
    return existingVisitorId;
  }

  const immortalVisitorId = normalizeVisitorIdCandidate(readCookie(legacyImmortalVisitorCookieName));
  if (immortalVisitorId) {
    writeCookie(visitorCookieName, immortalVisitorId);
    return immortalVisitorId;
  }

  const visitorId = uuidv7();
  writeCookie(visitorCookieName, visitorId);
  return visitorId;
}

// Anonymous visitor id used while opted out of PII tracking. It is never read from or written to any
// cookie/storage, so opted-out visits leave no persistent identity and cannot be linked across loads.
export function getEphemeralVisitorId(): string {
  return uuidv7();
}

// Persists an id (typically the ephemeral id of the current visit) once the visitor opts in, so the
// session isn't split at the moment consent is granted.
export function persistVisitorId(visitorId: string): void {
  writeCookie(visitorCookieName, visitorId);
}

export function clearPersistentVisitorId(): void {
  deleteCookie(visitorCookieName);
}
